import { CircularProgress, Backdrop, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IRequestType } from '../../constants/interfaces';
import { useStyles } from '../HomePage/HomePage.styles';
import { useRequestTypesContext } from '../../contexts/RequestTypesContext';
import { useLayout } from '../../contexts/LayoutContext';
import RequestFormView from '../HomePage/components/RequestFormView';

export default function RequestPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { requestTypeId } = useParams<{ requestTypeId: string }>();
  const {
    sortedRequestTypes,
    loading: requestLoading,
    error,
  } = useRequestTypesContext();
  const [selectedRequestType, setSelectedRequestType] =
    useState<IRequestType | null>(null);
  const { useGrid, setSelectedRequestType: setLayoutSelectedRequestType } =
    useLayout();

  // Find and set the request type based on URL parameter
  useEffect(() => {
    if (requestTypeId && sortedRequestTypes.length > 0) {
      const requestType = sortedRequestTypes.find(
        (rt) => rt.id.toString() === requestTypeId
      );
      if (requestType) {
        setSelectedRequestType(requestType);
        setLayoutSelectedRequestType(requestType); // Also set in layout context
      } else {
        // Invalid request type ID, redirect to home
        setSelectedRequestType(null);
        setLayoutSelectedRequestType(null);
        navigate('/', { replace: true });
      }
    }
  }, [
    requestTypeId,
    sortedRequestTypes,
    navigate,
    setLayoutSelectedRequestType,
  ]);

  // Clear selected request type when component unmounts
  useEffect(() => {
    return () => {
      setSelectedRequestType(null);
      setLayoutSelectedRequestType(null);
    };
  }, [setLayoutSelectedRequestType]);

  // Determine what to show based on state
  const showLoading =
    requestLoading ||
    (sortedRequestTypes.length > 0 && !selectedRequestType && !error);
  const showError = error;
  const hasRequestTypeId = Boolean(requestTypeId);

  return (
    <>
      <Backdrop
        open={showLoading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      {showError && (
        <Box
          className={classes.errorCenter}
          sx={{ borderRadius: 1, color: 'error.dark', p: 2 }}
        >
          {error}
        </Box>
      )}

      {hasRequestTypeId &&
        !showError &&
        !showLoading &&
        selectedRequestType && (
          <RequestFormView
            requestType={selectedRequestType}
            useGrid={useGrid}
          />
        )}
    </>
  );
}
